import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PaperShapes from './PaperShapes';
import { sectionLabel } from './homepageContent';

/**
 * Searches band — the "someone was listening" section.
 *
 * SIGNATURE: a live typing terminal. The advocate's own 11 pm search types
 * itself character by character with a blinking caret; when it lands, the
 * answer resolves beside it and the shipped feature is named in brass. You
 * watch the question being asked, then answered — the section's whole claim,
 * enacted rather than described.
 *
 * Grouped by practice (Criminal / Civil / Corporate) so an advocate finds
 * their own world instead of scanning five unsorted rows. Tabs auto-advance,
 * reusing the hero's rotating-arc vocabulary so the motion reads as native to
 * the page rather than bolted on. Clicking a tab pins it.
 *
 * HONESTY CONTRACT: every query must be answered by a SHIPPED feature — each
 * `route` below has a live entry in backend/app/constants/feature_registry.py.
 * This section may never advertise something that does not exist (that is how
 * the old WorkflowsDocket went wrong). No invented numbers, no testimonials:
 * recognition of the pain is the only social proof used.
 *
 * Motion is fully disabled under prefers-reduced-motion — queries render
 * complete, no caret, no auto-advance.
 */

interface SearchRow {
  query: string;
  feature: string;
  answer: string;
}

interface PracticeSearches {
  tab: string;
  /** Vertical marginalia: what this practice's corpus is grounded in. */
  corpus: string;
  rows: SearchRow[];
}

const PRACTICES: PracticeSearches[] = [
  {
    tab: 'Criminal',
    corpus: 'BNS · BNSS · BSA',
    rows: [
      {
        query: 'ipc 420 equivalent in bns',
        feature: 'IPC → BNS Mapper',
        answer:
          'All 570 official mappings, both directions, from the Government concordance — not a blog table.',
      },
      {
        query: 'is bns 103 bailable or not',
        feature: 'Bail Grounds Checklist',
        answer:
          'Bailable or not, cognizable or not, and what the section carries — grounded in the Act, flagged when unverified.',
      },
      {
        query: 'fir has sections that do not fit the facts',
        feature: 'Analyze FIR',
        answer:
          'Reads the FIR, flags where the charge is heavier than the facts carry, and drafts the defence line.',
      },
    ],
  },
  {
    tab: 'Civil',
    corpus: 'CPA · ARBITRATION · LIMITATION',
    rows: [
      {
        query: 'limitation for consumer complaint district forum',
        feature: 'Forum & Limitation',
        answer:
          'The forum and the deadline, computed by rule — never by AI guesswork.',
      },
      {
        query: 'how to reply to legal notice recovery of money',
        feature: 'Analyze Notice',
        answer:
          'Reads the notice, extracts the demands, and returns a response strategy that cites its sources.',
      },
      {
        query: 'sc judgment anticipatory bail after chargesheet',
        feature: 'Search Cases',
        answer:
          '42,858 Supreme Court and over 1.7 crore High Court judgments, in one place.',
      },
    ],
  },
  {
    tab: 'Corporate',
    corpus: 'COMPANIES ACT · SEBI · IBC',
    rows: [
      {
        query: 'due date sh-4 share transfer stamp duty',
        feature: 'Share Transfers',
        answer:
          'The statutory clock starts when the instrument is executed — recorded, tracked, and never quietly missed.',
      },
      {
        query: 'annual filing due dates for this company',
        feature: 'Compliance',
        answer:
          'The statutory calendar with registers, filings and clocks — ranked by which additional fee is about to step.',
      },
      {
        query: 'who held what shares on the closing date',
        feature: 'Cap Table',
        answer:
          'Instrument-level holdings, reconstructable as at any past date — not a spreadsheet anyone has to trust.',
      },
    ],
  },
];

const TYPE_MS = 42;
const HOLD_MS = 2100;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

export default function SearchesBand() {
  const [practice, setPractice] = useState(0);
  const [row, setRow] = useState(0);
  const [typed, setTyped] = useState('');
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  // Bumped on tab click so the typing sequence restarts cleanly from row 0.
  const [epoch, setEpoch] = useState(0);
  const timers = useRef<number[]>([]);

  const rows = PRACTICES[practice].rows;
  const current = rows[row];
  const settled = typed.length === current.query.length;

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  }, []);

  // Type the current query one character at a time, hold it so the answer can
  // be read, then advance — cycling rows within a practice, then to the next
  // practice. Under reduced motion the query is simply shown complete.
  useEffect(() => {
    clearTimers();

    if (reducedMotion) {
      setTyped(current.query);
      return undefined;
    }

    setTyped('');
    for (let i = 1; i <= current.query.length; i += 1) {
      timers.current.push(
        window.setTimeout(() => setTyped(current.query.slice(0, i)), i * TYPE_MS),
      );
    }

    const done = current.query.length * TYPE_MS;
    timers.current.push(
      window.setTimeout(() => {
        if (row < rows.length - 1) {
          setRow((r) => r + 1);
        } else {
          setRow(0);
          setPractice((p) => (p + 1) % PRACTICES.length);
        }
      }, done + HOLD_MS),
    );

    return clearTimers;
  }, [practice, row, epoch, reducedMotion, current.query, rows.length, clearTimers]);

  const pinPractice = (index: number) => {
    setPractice(index);
    setRow(0);
    setEpoch((e) => e + 1);
  };

  // Reserve the tallest row's height so the answer panel never jolts the
  // page as rows cycle.
  const tallestAnswer = useMemo(
    () =>
      Math.max(
        ...PRACTICES.flatMap((p) => p.rows.map((r) => r.answer.length)),
      ),
    [],
  );

  return (
    <section
      id="searches"
      className="relative scroll-mt-16 overflow-hidden border-t border-brass-border bg-parchment px-5 py-20 text-chamber-ink sm:px-8 lg:px-14 lg:py-24"
    >
      <PaperShapes />

      <div className="relative lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center lg:gap-16">
        {/* The claim */}
        <div>
          <span className="font-plex text-[11px] tracking-[0.3em] text-brass-deep">
            {sectionLabel('searches')}
          </span>
          <h2 className="mt-4 font-display text-3xl font-normal leading-[1.12] text-chamber-ink lg:text-[44px]">
            You&apos;ve searched for this before.{' '}
            <em className="font-editorial italic text-brass-ink">
              At 11 pm. The night before.
            </em>
          </h2>
          <p className="mt-5 max-w-[480px] text-base leading-[1.7] text-parchment-muted">
            Every tool in iAdvocate began as a question advocates were already
            typing into a search bar. Now the answer is grounded in the Act and
            the judgment — not a forum thread.
          </p>

          {/* Practice tabs — chips, like the journey's chapter strip. */}
          <div className="mt-8 flex flex-wrap items-center gap-2.5">
            {PRACTICES.map((p, i) => (
              <button
                key={p.tab}
                type="button"
                onClick={() => pinPractice(i)}
                aria-current={i === practice ? 'true' : undefined}
                className={`cursor-pointer rounded-xl px-4 py-2 font-instrument text-[14px] font-medium transition-colors ${
                  i === practice
                    ? 'bg-chamber-ink text-parchment shadow-[0_8px_18px_-10px_rgba(60,45,15,.55)]'
                    : 'bg-parchment-hover text-chamber-ink/70 hover:text-chamber-ink'
                }`}
              >
                {p.tab}
              </button>
            ))}
          </div>
          <p className="mt-3 hidden font-plex text-[10px] tracking-[0.24em] text-parchment-muted sm:block">
            {PRACTICES[practice].corpus}
          </p>

          <p className="mt-10 hidden font-editorial text-[19px] italic text-brass-ink lg:block">
            Someone was listening.
          </p>
        </div>

        {/* The search, then its answer — paper above, ink below. */}
        <div className="mt-10 lg:mt-0">
          <div className="rounded-2xl bg-parchment-card px-5 pb-4 pt-5 shadow-[0_1px_1px_rgba(60,45,15,.10),0_16px_30px_-12px_rgba(60,45,15,.42)] sm:px-7 sm:pt-6">
            <p className="font-plex text-[10.5px] tracking-[0.28em] text-brass-deep">
              SEARCH
            </p>
            <div className="mt-3 flex items-baseline gap-3 border-b border-parchment-border pb-4">
              <span aria-hidden="true" className="font-plex text-sm text-brass-ink">
                ⌕
              </span>
              <p
                className="break-words font-plex text-[15px] text-chamber-ink sm:text-[18px]"
                aria-live="polite"
              >
                {typed}
                {!reducedMotion && (
                  <span
                    aria-hidden="true"
                    className={`ml-0.5 inline-block h-[16px] w-[7px] translate-y-[2px] bg-brass-ink ${
                      settled ? 'animate-pulse' : ''
                    }`}
                  />
                )}
              </p>
            </div>

            {/* Progress — which of this practice's searches is on screen. */}
            <div className="flex gap-1.5 pt-4">
              {rows.map((r, i) => (
                <button
                  key={r.query}
                  type="button"
                  onClick={() => {
                    setRow(i);
                    setEpoch((e) => e + 1);
                  }}
                  aria-label={`Show search: ${r.query}`}
                  aria-current={i === row ? 'true' : undefined}
                  className={`h-[3px] flex-1 cursor-pointer rounded-full transition-colors ${
                    i === row
                      ? 'bg-chamber-ink'
                      : 'bg-parchment-border-strong hover:bg-brass/60'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Answer — resolves once the query has finished typing. */}
          <div
            className="relative mt-3 rounded-2xl bg-chamber-ink px-5 py-5 text-parchment shadow-[0_1px_1px_rgba(0,0,0,.2),0_18px_34px_-12px_rgba(20,25,15,.55)] sm:px-7 sm:py-6 lg:ml-10"
            style={{ minHeight: `${Math.ceil(tallestAnswer / 62) * 26 + 84}px` }}
          >
            <div
              key={`${practice}-${row}`}
              className={`transition-opacity duration-500 ${
                settled ? 'opacity-100' : 'opacity-0'
              } ${settled ? 'animate-chamber-fade-up motion-reduce:animate-none' : ''}`}
            >
              <p className="font-plex text-[10.5px] tracking-[0.28em] text-brass">
                ANSWERED BY
              </p>
              <p className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span aria-hidden="true" className="text-brass">
                  →
                </span>
                <span className="font-display text-[22px] text-parchment sm:text-[26px]">
                  {current.feature}
                </span>
              </p>
              <p className="mt-2 max-w-[560px] text-[14px] leading-[1.65] text-parchment/75 sm:text-[15px]">
                {current.answer}
              </p>
            </div>
          </div>

          <p className="mt-8 text-right font-editorial text-[19px] italic text-brass-ink lg:hidden">
            Someone was listening.
          </p>
        </div>
      </div>
    </section>
  );
}
